import { Link } from "react-router-dom";
import Header from "../components/Header";
import { ArrowLeft, FileText, Users, CreditCard, AlertTriangle, Scale, Ban, Clock } from "lucide-react";

export default function TermsConditions({ user, logout }) {
  return (
    <div className="min-h-screen bg-background">
      <Header user={user} logout={logout} />
      
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Link */}
          <Link 
            to="/" 
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-8"
            data-testid="back-to-home"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver al inicio
          </Link>

          {/* Header */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <FileText className="h-6 w-6 text-primary" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="terms-title">
                Términos y Condiciones
              </h1>
            </div>
            <p className="text-muted-foreground">
              Última actualización: Enero 2025
            </p>
          </div>

          {/* Content */}
          <div className="prose prose-gray max-w-none space-y-8">
            
            {/* Intro */}
            <section className="bg-gray-50 rounded-2xl p-6 border" data-testid="terms-intro">
              <p className="text-lg leading-relaxed">
                Bienvenido a <strong>BulkBytes</strong>. Al utilizar nuestra plataforma de compras grupales, 
                aceptas estos términos y condiciones. Por favor, léelos atentamente antes de realizar cualquier compra.
              </p>
            </section>

            {/* Section 1 */}
            <section data-testid="terms-section-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-blue-600" />
                </div>
                <h2 className="text-xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  1. Definiciones
                </h2>
              </div>
              <div className="ml-13 space-y-3">
                <p className="text-muted-foreground">
                  <strong>"Plataforma":</strong> El sitio web y servicios de BulkBytes.
                </p>
                <p className="text-muted-foreground">
                  <strong>"Usuario":</strong> Cualquier persona que accede o utiliza la plataforma.
                </p>
                <p className="text-muted-foreground">
                  <strong>"Oferta Grupal" o "Paquete":</strong> Una oferta de producto con un precio especial 
                  que requiere un número mínimo de participantes para confirmarse.
                </p>
                <p className="text-muted-foreground">
                  <strong>"Participación":</strong> La adhesión de un usuario a una oferta grupal mediante pago.
                </p>
              </div>
            </section>

            {/* Section 2 */}
            <section data-testid="terms-section-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <Users className="h-5 w-5 text-green-600" />
                </div>
                <h2 className="text-xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  2. Funcionamiento del Servicio
                </h2>
              </div>
              <div className="ml-13 space-y-4">
                <div>
                  <h3 className="font-medium mb-2">2.1 Modelo de Compra Grupal</h3>
                  <ul className="list-disc list-inside text-muted-foreground space-y-1">
                    <li>BulkBytes ofrece productos electrónicos a precios reducidos mediante compras grupales.</li>
                    <li>Cada oferta tiene un número mínimo de participantes y un tiempo límite (24, 48 o 72 horas).</li>
                    <li>El precio solo se garantiza si se alcanza el mínimo de participantes antes del plazo.</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-medium mb-2">2.2 Confirmación de Compra</h3>
                  <ul className="list-disc list-inside text-muted-foreground space-y-1">
                    <li>Si se alcanza el umbral mínimo de participantes, la compra se confirma automáticamente.</li>
                    <li>Los usuarios recibirán una notificación de confirmación y los detalles de envío.</li>
                    <li>BulkBytes puede establecer un umbral de confirmación (ej. 80% del mínimo) para ciertas ofertas.</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-medium mb-2">2.3 No Confirmación</h3>
                  <ul className="list-disc list-inside text-muted-foreground space-y-1">
                    <li>Si no se alcanza el mínimo de participantes, la oferta se cancela.</li>
                    <li>En este caso, se procede al reembolso automático del importe pagado.</li>
                    <li>El reembolso se realiza por el mismo método de pago utilizado.</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 3 */}
            <section data-testid="terms-section-3">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <CreditCard className="h-5 w-5 text-purple-600" />
                </div>
                <h2 className="text-xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  3. Pagos y Precios
                </h2>
              </div>
              <div className="ml-13 space-y-4">
                <p className="text-muted-foreground">
                  <strong>3.1</strong> Todos los precios se muestran en euros (€) e incluyen IVA.
                </p>
                <p className="text-muted-foreground">
                  <strong>3.2</strong> El pago se realiza en el momento de unirse a una oferta grupal.
                </p>
                <p className="text-muted-foreground">
                  <strong>3.3</strong> Los pagos son procesados de forma segura por nuestro proveedor de pagos. BulkBytes no almacena 
                  datos de tarjetas de crédito.
                </p>
                <p className="text-muted-foreground">
                  <strong>3.4</strong> El precio final puede incluir gastos de envío, que se especificarán 
                  claramente antes de confirmar la participación.
                </p>
              </div>
            </section>

            {/* Section 4 */}
            <section data-testid="terms-section-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-orange-600" />
                </div>
                <h2 className="text-xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  4. Envíos y Entregas
                </h2>
              </div>
              <div className="ml-13 space-y-4">
                <p className="text-muted-foreground">
                  <strong>4.1</strong> Una vez confirmada la oferta grupal, los productos se envían en el plazo 
                  indicado en cada oferta (típicamente 7-15 días laborables).
                </p>
                <p className="text-muted-foreground">
                  <strong>4.2</strong> Los envíos se realizan únicamente a direcciones dentro de España peninsular, 
                  salvo que se indique lo contrario.
                </p>
                <p className="text-muted-foreground">
                  <strong>4.3</strong> El usuario es responsable de proporcionar una dirección de envío correcta 
                  y completa.
                </p>
                <p className="text-muted-foreground">
                  <strong>4.4</strong> En caso de ausencia durante la entrega, se aplicarán las políticas del 
                  transportista contratado.
                </p>
              </div>
            </section>

            {/* Section 5 */}
            <section data-testid="terms-section-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                  <Ban className="h-5 w-5 text-red-600" />
                </div>
                <h2 className="text-xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  5. Cancelaciones
                </h2>
              </div>
              <div className="ml-13 space-y-4">
                <p className="text-muted-foreground">
                  <strong>5.1</strong> Una vez realizado el pago para unirse a una oferta grupal, <strong>no es 
                  posible cancelar la participación de forma voluntaria</strong>.
                </p>
                <p className="text-muted-foreground">
                  <strong>5.2</strong> Esta política es necesaria para garantizar el funcionamiento del modelo 
                  de compra grupal, donde cada participante es esencial para alcanzar el mínimo requerido.
                </p>
                <p className="text-muted-foreground">
                  <strong>5.3</strong> El reembolso solo se produce automáticamente si la oferta grupal no alcanza 
                  el mínimo de participantes en el plazo establecido.
                </p>
              </div>
            </section>

            {/* Section 6 */}
            <section data-testid="terms-section-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                  <AlertTriangle className="h-5 w-5 text-yellow-600" />
                </div>
                <h2 className="text-xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  6. Responsabilidades del Usuario
                </h2>
              </div>
              <div className="ml-13">
                <p className="text-muted-foreground mb-4">El usuario se compromete a:</p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li>Proporcionar información veraz y actualizada</li>
                  <li>Utilizar la plataforma de forma lícita y conforme a estos términos</li>
                  <li>No realizar actividades fraudulentas o que perjudiquen a otros usuarios</li>
                  <li>Mantener la confidencialidad de sus credenciales de acceso</li>
                  <li>No utilizar la plataforma para fines comerciales no autorizados</li>
                </ul>
              </div>
            </section>

            {/* Section 7 */}
            <section data-testid="terms-section-7">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                  <Scale className="h-5 w-5 text-gray-600" />
                </div>
                <h2 className="text-xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  7. Limitación de Responsabilidad
                </h2>
              </div>
              <div className="ml-13 space-y-4">
                <p className="text-muted-foreground">
                  <strong>7.1</strong> BulkBytes actúa como intermediario entre compradores y proveedores de 
                  productos electrónicos.
                </p>
                <p className="text-muted-foreground">
                  <strong>7.2</strong> No nos hacemos responsables de defectos de fabricación, que serán cubiertos 
                  por la garantía del fabricante.
                </p>
                <p className="text-muted-foreground">
                  <strong>7.3</strong> No garantizamos que todas las ofertas grupales se completen, ya que dependen 
                  de alcanzar el número mínimo de participantes.
                </p>
                <p className="text-muted-foreground">
                  <strong>7.4</strong> Nos reservamos el derecho de cancelar ofertas en caso de errores manifiestos 
                  en precios o disponibilidad.
                </p>
              </div>
            </section>

            {/* Section 8 */}
            <section data-testid="terms-section-8">
              <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                8. Propiedad Intelectual
              </h2>
              <p className="text-muted-foreground">
                Todos los contenidos de la plataforma (textos, imágenes, logotipos, diseño) son propiedad de 
                BulkBytes o de terceros licenciantes. Queda prohibida su reproducción, distribución o 
                modificación sin autorización expresa.
              </p>
            </section>

            {/* Section 9 */}
            <section data-testid="terms-section-9">
              <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                9. Modificaciones
              </h2>
              <p className="text-muted-foreground">
                BulkBytes se reserva el derecho de modificar estos términos en cualquier momento. Los cambios 
                serán efectivos desde su publicación en la plataforma. El uso continuado del servicio implica 
                la aceptación de los términos modificados.
              </p>
            </section>

            {/* Section 10 */}
            <section data-testid="terms-section-10">
              <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                10. Legislación Aplicable
              </h2>
              <p className="text-muted-foreground">
                Estos términos se rigen por la legislación española. Para cualquier controversia, las partes 
                se someten a los juzgados y tribunales de la ciudad de Madrid, con renuncia expresa a cualquier 
                otro fuero que pudiera corresponderles.
              </p>
            </section>

            {/* Contact */}
            <section className="bg-primary/5 rounded-2xl p-6 border border-primary/20" data-testid="terms-contact">
              <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Contacto
              </h2>
              <p className="text-muted-foreground">
                Para cualquier consulta sobre estos términos y condiciones:
              </p>
              <div className="mt-4 space-y-2">
                <p><strong>Email:</strong> bulkbytes26@gmail.com</p>
                <p><strong>Empresa:</strong> BulkBytes S.L.</p>
                <p><strong>CIF:</strong> B22503882</p>
                <p><strong>Ubicación:</strong> Tarragona, España</p>
              </div>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 border-t">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-muted-foreground">
            © 2025 BulkBytes. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
}
